/** @file Image pixels to skeleton space per image, as autorig places them (PSD origin, else on the root). */
import type { Doc } from './schema';

/** An affine [a, b, c, d, tx, ty] in math/affine's layout. */
type Placement = [number, number, number, number, number, number];

/**
 * Per image id, the affine that maps the image's pixels (u, v) (top-left origin, y down) into skeleton space (y up).
 * A PSD-placed image (`origin`) goes to (u + x − ax, ay − y − v), (ax, ay) being the bottom centre of the union of
 * every PSD-placed image (ax rounded); an image without `origin` stands with its bottom centre on the root:
 * (u − floor(w / 2), h − v). autorig's skeleton and `bone.setTransform { space: 'image' }` read this one rule.
 */
export function imagePlacement(doc: Doc): Map<string, Placement> {
  let left = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;
  for (const img of Object.values(doc.images)) {
    if (img.origin === undefined) continue;
    left = Math.min(left, img.origin.x);
    right = Math.max(right, img.origin.x + img.w);
    bottom = Math.max(bottom, img.origin.y + img.h);
  }
  const placed = bottom !== Number.NEGATIVE_INFINITY;
  const ax = placed ? Math.round((left + right) / 2) : 0;
  const ay = placed ? bottom : 0;
  const out = new Map<string, Placement>();
  for (const [id, img] of Object.entries(doc.images)) {
    out.set(id, img.origin ? [1, 0, 0, -1, img.origin.x - ax, ay - img.origin.y] : [1, 0, 0, -1, -Math.floor(img.w / 2), img.h]);
  }
  return out;
}
